import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { addTerritory, updateTerritory } from "../../actions/userActions/territoryAction";
import { getCities } from "../../actions/userActions/cityAction";
import { IconButton } from "@mui/material";
import { Map, Check } from "lucide-react";
import closeModal from "../../assets/images/closeModal.svg";
import BodyLargeMedium from "../designSystem/BodyLargeMedium";
import BodyExtraSmallMedium from "../designSystem/BodyExtraSmallMedium";
import TextField from "../designSystem/TextField";
import SingleSelect from "../designSystem/SingleSelect";
import MultiSelect from "../designSystem/MultiSelect";
import LoadingOverlay from "../loadingOverlay/LoadingOverlay";
import CancelButton from "../subComponents/buttons/CustomCancelButton";
import PrimaryButton from "../subComponents/buttons/PrimaryButton";
import { validateFields } from "../../utils/Validations";
import UserService from "../../services/user.service";

function AddTerritory({ onClose, editData, onSave }) {
  const dispatch = useDispatch();

  const initialValues = {
    id: 0,
    name: "",
    cityId: null,
    practiceLocationIds: [],
    isActive: true,
  };

  const [formValues, setFormValues] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [practiceLocationOptions, setPracticeLocationOptions] = useState([]);
  const [isPracticeLocLoading, setIsPracticeLocLoading] = useState(false);

  const allCities = useSelector((state) => state.cities_reducer.cities);

  // Fetch cities for the single-select
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await dispatch(
          getCities({ filter: { countryId: "1" }, pageNumber: -1, pageSize: 0 })
        );
        if (editData) {
          // Map practiceLocations from GetAll response to IDs and options
          const existingPracticeLocations = editData.practiceLocations || [];
          const populated = {
            id: editData.id,
            name: editData.name,
            cityId: editData.city?.cityId || null,
            practiceLocationIds: existingPracticeLocations.map((pl) => pl.practiceLocationId),
            isActive: editData.isActive,
          };
          setFormValues(populated);
          // Seed options immediately from response so selections show without waiting for fetch
          setPracticeLocationOptions(
            existingPracticeLocations.map((pl) => ({
              value: pl.practiceLocationId,
              label: pl.practiceLocationName,
            }))
          );
          // Then fetch full list for the city, excluding locations owned by other territories
          if (editData.city?.cityId) {
            await fetchPracticeLocationsByCity(editData.city.cityId, editData.id);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [editData]);

  // Fetch practice locations for a city, excluding ones already assigned to other territories.
  // Pass currentTerritoryId in edit mode so the territory's own locations remain selectable.
  const fetchPracticeLocationsByCity = async (cityId, currentTerritoryId = null) => {
    if (!cityId) {
      setPracticeLocationOptions([]);
      return;
    }
    setIsPracticeLocLoading(true);
    try {
      const [plRes, terrRes] = await Promise.all([
        UserService.getPracticeLocationsByCity(cityId),
        UserService.getTerritories(-1, 0, cityId, undefined, undefined),
      ]);

      // Build set of practice location IDs already assigned to other territories
      const assignedIds = new Set();
      if (terrRes.data.status && terrRes.data.result) {
        terrRes.data.result.forEach((territory) => {
          if (territory.id === currentTerritoryId) return; // skip self in edit mode
          (territory.practiceLocations || []).forEach((pl) => {
            assignedIds.add(pl.practiceLocationId);
          });
        });
      }

      if (plRes.data.status && plRes.data.result) {
        setPracticeLocationOptions(
          plRes.data.result
            .filter((pl) => !assignedIds.has(pl.id))
            .map((pl) => ({ value: pl.id, label: pl.name }))
        );
      } else {
        setPracticeLocationOptions([]);
      }
    } catch (e) {
      console.error("Error fetching practice locations:", e);
      setPracticeLocationOptions([]);
    } finally {
      setIsPracticeLocLoading(false);
    }
  };

  const cityOptions = (() => {
    if (!allCities || allCities === "isLoading" || !allCities.result) return [];
    return allCities.result
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((city) => ({ value: city.id, label: city.name }));
  })();

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleCityChange = (option) => {
    // Reset practice locations when city changes
    setFormValues({ ...formValues, cityId: option.value, practiceLocationIds: [] });
    setPracticeLocationOptions([]);
    // Pass current territory ID so in edit mode its own locations stay available
    fetchPracticeLocationsByCity(option.value, formValues.id || null);
  };

  const handlePracticeLocChange = (updatedValues) => {
    setFormValues({ ...formValues, practiceLocationIds: updatedValues });
  };

  const handleActiveChange = (e) => {
    setFormValues({ ...formValues, isActive: e.target.checked });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateFields(formValues, "territory");
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaveLoading(true);
    try {
      if (editData) {
        await dispatch(updateTerritory(formValues));
      } else {
        await dispatch(addTerritory(formValues));
      }
      setSaveLoading(false);
      onSave();
    } catch (error) {
      console.error("Error saving territory:", error);
      setSaveLoading(false);
    }
  };

  return (
    <div className="dynamicmodal-overlay">
      <div className="dynamicmodal-container overlay-wrapper">
        <LoadingOverlay isVisible={loading || saveLoading} />
        <div className={loading || saveLoading ? "blur" : ""}>
          <div className="dynamicmodal-header">
            <div className="dynamicmodal-header-left">
              <div className="icon">
                <Map size={20} strokeWidth={1.5} color="white" />
              </div>
              <div className="text">
                <h2 className="modalheading">
                  {editData ? "Edit Territory" : "Add Territory"}
                </h2>
                <p className="modaldescription">
                  {editData
                    ? "Update the territory below"
                    : "Enter a territory below"}
                </p>
              </div>
            </div>
            <div className="dynamicmodal-header-right">
              <IconButton onClick={onClose}>
                <img src={closeModal} alt="close" />
              </IconButton>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div className="dynamicmodal-body">
            <TextField
              name="name"
              label="Name*"
              type="text"
              value={formValues.name}
              onChange={handleTextChange}
              placeholder="Name"
              error={formErrors.name}
            />

            <SingleSelect
              options={cityOptions}
              value={formValues.cityId}
              onChange={handleCityChange}
              label="City*"
              placeholder="City"
              isSearchable
              disabled={!!editData}
              error={formErrors.cityId}
            />

            <MultiSelect
              options={practiceLocationOptions}
              value={formValues.practiceLocationIds}
              onChange={handlePracticeLocChange}
              label="Practice Locations*"
              placeholder={
                !formValues.cityId
                  ? "Select a city first"
                  : isPracticeLocLoading
                  ? "Loading..."
                  : "Select"
              }
              isSearchable
              isLoading={isPracticeLocLoading}
              disabled={!formValues.cityId}
              error={formErrors.practiceLocationIds}
            />

            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
              <input
                type="checkbox"
                id="isActive"
                checked={formValues.isActive}
                onChange={handleActiveChange}
                style={{ cursor: "pointer", width: "16px", height: "16px" }}
              />
              <label
                htmlFor="isActive"
                style={{ cursor: "pointer", margin: 0, fontSize: "14px" }}
              >
                Active?
              </label>
            </div>
          </div>

          <div className="dynamicmodal-footer">
            <CancelButton onClick={onClose} text="Cancel" />
            <PrimaryButton text="Save" icon={Check} />
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddTerritory;
